import { cn } from "@/lib/utils"
import { useState, useEffect, useCallback, useRef } from "react"
import { useTranslation } from "react-i18next"

const LOADING_PHRASE_KEYS = [
  "loading.phrase_1",
  "loading.phrase_2",
  "loading.phrase_3",
  "loading.phrase_4",
  "loading.phrase_5",
  "loading.phrase_6",
  "loading.phrase_7",
  "loading.phrase_8",
  "loading.phrase_9",
  "loading.phrase_10",
  "loading.phrase_11",
  "loading.phrase_12",
  "loading.phrase_13",
  "loading.phrase_14",
  "loading.phrase_15",
  "loading.phrase_16",
  "loading.phrase_17",
  "loading.phrase_18",
  "loading.phrase_19",
  "loading.phrase_20",
] as const

const sizeMap = {
  sm: { width: 32, height: 23 },
  md: { width: 80, height: 58 },
  lg: { width: 140, height: 101 },
} as const

type SpinnerSize = keyof typeof sizeMap

function Spinner({
  size = "sm",
  className,
  ...props
}: { size?: SpinnerSize } & Omit<React.ComponentProps<"div">, "children">) {
  const dims = sizeMap[size]
  return (
    <div
      role="status"
      aria-label="Loading"
      className={cn("ds-spinner inline-flex items-center justify-center", className)}
      {...props}
    >
      <svg
        className="ds-speedo"
        width={dims.width}
        height={dims.height}
        viewBox="0 0 220 155"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M 18 138 A 94 94 0 0 1 202 138"
          fill="none"
          className="ds-track"
          strokeWidth="14"
          strokeLinecap="round"
        />
        <path
          d="M 18 138 A 94 94 0 0 1 202 138"
          fill="none"
          strokeWidth="22"
          strokeLinecap="round"
          className="ds-arc-amb"
        />
        <path
          d="M 18 138 A 94 94 0 0 1 202 138"
          fill="none"
          strokeWidth="14"
          strokeLinecap="round"
          className="ds-arc-main"
        />
        <path
          d="M 18 138 A 94 94 0 0 1 202 138"
          fill="none"
          className="ds-cutout"
          strokeWidth="7"
          strokeLinecap="round"
        />
        <g className="ds-ticks" strokeLinecap="round">
          <line x1="110" y1="14" x2="110" y2="28" strokeWidth="2.5" />
          <line x1="170" y1="32" x2="163" y2="44" strokeWidth="2" />
          <line x1="201" y1="84" x2="188" y2="88" strokeWidth="2" />
          <line x1="50" y1="32" x2="57" y2="44" strokeWidth="2" />
          <line x1="19" y1="84" x2="32" y2="88" strokeWidth="2" />
        </g>
        <g className="ds-ticks-minor" strokeLinecap="round">
          <line x1="142" y1="17" x2="139" y2="29" strokeWidth="1.5" />
          <line x1="189" y1="57" x2="179" y2="64" strokeWidth="1.5" />
          <line x1="78" y1="17" x2="81" y2="29" strokeWidth="1.5" />
          <line x1="31" y1="57" x2="41" y2="64" strokeWidth="1.5" />
        </g>
        <g className="ds-needle">
          <line
            x1="110" y1="120" x2="110" y2="32"
            className="ds-needle-line"
            strokeWidth="2.5" strokeLinecap="round"
          />
          <line
            x1="110" y1="120" x2="110" y2="134"
            className="ds-needle-line"
            strokeWidth="2.5" strokeLinecap="round" opacity="0.15"
          />
        </g>
        <circle cx="110" cy="120" r="10" className="ds-pivot-ring" strokeWidth="1.5" />
        <circle cx="110" cy="120" r="5.5" className="ds-pivot-fill" />
        <circle cx="110" cy="120" r="2.5" className="ds-pivot-dot" />
      </svg>
    </div>
  )
}

function PageLoader({ className }: { className?: string }) {
  const { t } = useTranslation()
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const getRandomIndex = useCallback(() => {
    return Math.floor(Math.random() * LOADING_PHRASE_KEYS.length)
  }, [])

  const [keyIndex, setKeyIndex] = useState(getRandomIndex)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false)
      timeoutRef.current = setTimeout(() => {
        setKeyIndex((prev) => {
          let next = getRandomIndex()
          while (next === prev && LOADING_PHRASE_KEYS.length > 1) {
            next = getRandomIndex()
          }
          return next
        })
        setVisible(true)
      }, 400)
    }, 3000)
    return () => {
      clearInterval(interval)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [getRandomIndex])

  return (
    <div className={cn("ds-spinner-bg", className)}>
      <div className="ds-spinner-circle">
        <Spinner size="lg" />
      </div>
      <p
        className={cn(
          "mt-5 h-5 text-sm text-primary font-medium text-center tracking-wide transition-opacity duration-300 ease-in-out",
          visible ? "opacity-100" : "opacity-0"
        )}
      >
        {t(LOADING_PHRASE_KEYS[keyIndex])}
      </p>
    </div>
  )
}

export { Spinner, PageLoader }
